import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "system/store";
import { Icon } from "@fluentui/react/lib/Icon";
import { isFullPatternMatch, isPartialPatternMatch } from "components/modules/policy-editor/utils/stringExtensions";
import { useDebouncedCallback } from "use-debounce";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { WarningRegular } from "@fluentui/react-icons";

interface InheritedConfigModalProps {
  open: boolean;
  onClose: () => void;
  subjectName: string;
  initialRegex?: string;
  mode?: "add" | "edit";
  onSubmit?: (newRegex: string, newMatchingPackages: string[]) => void;
}

export const InheritedConfigModal: React.FC<InheritedConfigModalProps> = ({
  open,
  onClose,
  subjectName,
  initialRegex = "",
  mode = "add",
  onSubmit,
}) => {
  const [regex, setRegex] = useState("");
  const [isValidRegex, setIsValidRegex] = useState(true);
  const [hasSimilarity, setHasSimilarity] = useState(true);
  const [hasMatches, setHasMatches] = useState(0);
  const packages = useSelector((state: RootState) => state.policyEditor.packages);
  const dispatch = useDispatch();

  const debouncedHandleChange = useDebouncedCallback((newRegex: string) => {
    const similarity = isPartialPatternMatch(subjectName, newRegex, true);
    setHasSimilarity(similarity);

    if (isValidRegex) {
      const matchingPackages = packages.filter((pkg) => pkg.value && isFullPatternMatch(pkg.value, newRegex, true));
      setHasMatches(matchingPackages.length);
    }
  }, 800);

  useEffect(() => {
    if (initialRegex === "") {
      const parts = subjectName.split(" ");
      const initialRegex = parts.length > 1 ? `${parts[0]} ${parts[1]}` : parts[0] || "";
      setRegex(initialRegex);
      return;
    }
    setRegex(initialRegex);
    debouncedHandleChange(initialRegex);
  }, [debouncedHandleChange, initialRegex, subjectName]);

  const handleRegexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRegex = event.target.value;
    setRegex(newRegex);

    try {
      new RegExp(newRegex);
      setIsValidRegex(true);
    } catch {
      return setIsValidRegex(false);
    }

    if (isValidRegex) {
      debouncedHandleChange(newRegex);
    }
  };

  const handleSubmit = () => {
    if (isValidRegex && hasSimilarity && regex && hasMatches > 0) {
      const newMatchingPackages = packages.filter((pkg) => pkg.value && isFullPatternMatch(pkg.value, regex, true)).map((pkg) => pkg.value);
      if (mode === "edit" && onSubmit) {
        onSubmit(regex, newMatchingPackages);
      } else {
        dispatch(policyEditorActions.addInheritedConfiguration({ regex, matchingPackages: newMatchingPackages }));
      }
      onClose();
    }
  };

  const suggestionText = useMemo(() => subjectName.split(" ").slice(0, 1).join(" "), [subjectName]);
  const title = mode === "edit" ? "Edit Inherited Regex" : "Add Inherited Configurations";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#fff" }}>
        {title}
        <Tooltip title="Enter a regex to match multiple packages for inheritance. It should relate to the current subject.">
          <IconButton size="small">
            <Icon iconName="Info" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: "#1b1a19",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: "16px !important",
        }}
      >
        <TextField
          fullWidth
          label="Regex Expression"
          value={regex}
          onChange={handleRegexChange}
          variant="outlined"
          error={!isValidRegex || !hasSimilarity || hasMatches === 0}
          helperText={
            !isValidRegex
              ? "Invalid regex pattern"
              : !hasSimilarity
              ? `Regex should include terms like '${suggestionText}'`
              : hasMatches === 0
              ? "No packages match this regex"
              : `${hasMatches} packages match this regex`
          }
          InputProps={{
            endAdornment:
              isValidRegex && hasSimilarity && hasMatches > 0 ? (
                <Icon iconName="CheckMark" style={{ paddingLeft: "9px", color: "green" }} />
              ) : (
                <Icon iconName="ErrorBadge" style={{ paddingLeft: "9px", color: "#d32f2f" }} />
              ),
          }}
        />
        {!hasSimilarity && (
          <Typography variant="body2" color="error" mt={1}>
            Ensure the regex relates to "{suggestionText}" (e.g., includes similar terms).
          </Typography>
        )}

        {mode === "edit" && (
          <Typography variant="body2" style={{ color: "#b89f3e" }} mt={1}>
            <WarningRegular style={{ marginRight: "3px", verticalAlign: "text-bottom", color: "#b89f3e" }} />
            Warning: Changing this regex may delete some configurations if they no longer match the new packages.
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: "#1b1a19",
        }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!isValidRegex || !hasSimilarity || !regex || hasMatches === 0} variant="contained">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
